import React, { useState, useRef, useEffect, useCallback } from 'react';
import { VirtuosoGrid } from 'react-virtuoso';
import { motion } from "motion/react";
import { getSignedMediaUrl } from '@/lib/utils';
import CustomVideoPlayer from './CustomVideoPlayer';
import type { GalleryItemResponse } from '@/features/clubs/types/types';

interface GalleryGridProps {
  galleryItems: GalleryItemResponse[];
  onItemClick: (item: GalleryItemResponse, index: number) => void;
  loadMore: () => Promise<void>;
  hasMore: boolean;
  loadingMore: boolean;
}

interface GalleryItemProps {
  item: GalleryItemResponse;
  index: number;
  onItemClick: (item: GalleryItemResponse, index: number) => void;
}

const GalleryItem: React.FC<GalleryItemProps> = ({ item, index, onItemClick }) => {
  const [isHovered, setIsHovered] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const [hasError, setHasError] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  
  // Use thumbnail_id for list view, determine if it's a video from content_type
  const isVideo = item.content_type?.startsWith('video');
  const thumbnailUrl = item.thumbnail_id 
    ? getSignedMediaUrl(item.thumbnail_id, 'image').url 
    : getSignedMediaUrl(item.media_id, 'image').url;

  // Handle video play/pause on hover
  useEffect(() => {
    if (videoRef.current && isVideo) {
      if (isHovered) {
        videoRef.current.play().catch(console.error);
      } else {
        videoRef.current.pause();
        videoRef.current.currentTime = 0;
      }
    }
  }, [isHovered, isVideo]);

  const handleError = () => {
    setHasError(true);
  };

  const handleLoad = () => {
    setIsLoaded(true);
  };

  if (hasError) {
    return (
      <motion.div
        className="aspect-[4/3] bg-gray-800 flex items-center justify-center rounded-lg"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ duration: 0.3 }}
      >
        <span className="text-gray-500 text-sm">Media unavailable</span>
      </motion.div>
    );
  }

  return (
    <motion.div
      className="group relative aspect-[4/3] overflow-hidden rounded-xl cursor-pointer"
      whileHover={{ 
        scale: 1.01,
        transition: { duration: 0.2, ease: "easeOut" }
      }}
      whileTap={{ 
        scale: 0.98,
        transition: { duration: 0.1 }
      }}
      onHoverStart={() => setIsHovered(true)}
      onHoverEnd={() => setIsHovered(false)}
      onClick={() => onItemClick(item, index)}
    >
      {/* Media Content - Always show thumbnail image for list view */}
      {isVideo && isHovered ? (
        <CustomVideoPlayer
          src={getSignedMediaUrl(item.media_id, 'video').url}
          className="w-full h-full"
          autoPlay={isHovered}
          muted={true}
          loop={true}
          playsInline={true}
          onError={handleError}
          onLoad={handleLoad}
        />
      ) : (
        <motion.img
          src={thumbnailUrl}
          alt={`Gallery item ${index + 1}`}
          className="w-full h-full object-cover"
          style={{ opacity: isLoaded ? 1 : 0 }}
          onError={handleError}
          onLoad={handleLoad}
          whileHover={{ scale: 1.05 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        />
      )}

      {/* Loading State */}
      {!isLoaded && !hasError && (
        <div className="absolute inset-0 bg-gray-800 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-gray-600 border-t-white rounded-full animate-spin" />
        </div>
      )}

      {/* Hover Overlay */}
      <motion.div
        className="absolute inset-0 bg-gradient-to-t from-black/60 via-black/20 to-transparent flex items-center justify-center"
        initial={{ opacity: 0 }}
        whileHover={{ opacity: 1 }}
        transition={{ duration: 0.3, ease: "easeOut" }}
      >
        <motion.div 
          className="bg-white/20 backdrop-blur-md rounded-full p-3 border border-white/20"
          initial={{ scale: 0.9, opacity: 0 }}
          whileHover={{ scale: 1.05, opacity: 1 }}
          transition={{ duration: 0.2 }}
        >
          <motion.svg 
            className="w-6 h-6 text-white" 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
            initial={{ rotate: 0 }}
            whileHover={{ rotate: 90 }}
            transition={{ duration: 0.3 }}
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
          </motion.svg>
        </motion.div>
      </motion.div>

      {/* Video Play Indicator */}
      {isVideo && (
        <motion.div 
          className="absolute top-3 right-3"
          initial={{ scale: 0, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ delay: 0.2, duration: 0.3 }}
        >
          <motion.div 
            className="bg-black/60 backdrop-blur-md rounded-full p-2 border border-white/20"
            whileHover={{ scale: 1.2, backgroundColor: "rgba(0,0,0,0.8)" }}
            transition={{ duration: 0.2 }}
          >
            <motion.svg 
              className="w-4 h-4 text-white" 
              fill="currentColor" 
              viewBox="0 0 24 24"
              animate={{ scale: [1, 1.1, 1] }}
              transition={{ duration: 2, repeat: Infinity }}
            >
              <path d="M8 5v14l11-7z" />
            </motion.svg>
          </motion.div>
        </motion.div>
      )}
    </motion.div>
  );
};

// Grid item wrapper for virtuoso
const ItemWrapper: React.FC<{ children?: React.ReactNode }> = ({ children }) => (
  <div className="p-2">{children}</div>
);

// Grid list container
const ListContainer = React.forwardRef<HTMLDivElement, React.HTMLProps<HTMLDivElement>>(
  (props, ref) => (
    <div 
      ref={ref} 
      {...props} 
      className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4"
    />
  )
);
ListContainer.displayName = 'ListContainer';

const GalleryGrid: React.FC<GalleryGridProps> = ({ 
  galleryItems, 
  onItemClick, 
  loadMore, 
  hasMore,
  loadingMore 
}) => {
  const handleEndReached = useCallback(() => {
    if (hasMore && !loadingMore) {
      loadMore();
    }
  }, [hasMore, loadingMore, loadMore]);

  if (galleryItems.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-center">
        <div className="w-16 h-16 bg-gray-800 rounded-full flex items-center justify-center mb-4">
          <svg className="w-8 h-8 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
          </svg>
        </div>
        <h3 className="text-xl font-medium text-white mb-2">No Gallery Items</h3>
        <p className="text-gray-400">This club hasn't added any media to their gallery yet.</p>
      </div>
    );
  }

  return (
    <div className="px-4">
      <VirtuosoGrid
        useWindowScroll
        totalCount={galleryItems.length}
        components={{
          Item: ItemWrapper,
          List: ListContainer,
        }}
        endReached={handleEndReached}
        overscan={200}
        itemContent={(index) => {
          const item = galleryItems[index];
          return (
            <GalleryItem
              key={item.id}
              item={item}
              index={index}
              onItemClick={onItemClick}
            />
          );
        }}
      />
      
      {/* Loading more indicator */}
      {loadingMore && (
        <div className="flex justify-center py-8">
          <div className="w-8 h-8 border-2 border-gray-600 border-t-white rounded-full animate-spin" />
        </div>
      )}
    </div>
  );
};

export default GalleryGrid;
